/**
 * 上传编排器（Issue #198）。
 *
 * 把 grant → PUT → finalize 三段抽成纯函数，便于在 vitest 下直接断言，
 * 不依赖 React/Browser File API。客户端 UI 只在 finalize 成功后才把
 * 文档标记为已上传，从而避免出现 awaiting_upload 文档被误标记为已完成的
 * 错位状态。
 */
import type { ApiComponents } from '@jinzhuan/api-client';
import { meError, type MeFetchError } from './classify';

export type CustomerDocumentMeDto =
  ApiComponents['schemas']['CustomerDocumentMeDto'];
export type RequestCustomerDocumentUploadGrantDto =
  ApiComponents['schemas']['RequestCustomerDocumentUploadGrantDto'];
export type FinalizeCustomerDocumentUploadDto =
  ApiComponents['schemas']['FinalizeCustomerDocumentUploadDto'];
export type RequestUploadGrantResponseDto =
  ApiComponents['schemas']['RequestUploadGrantResponseDto'];

export type UploadBody = ArrayBuffer | Uint8Array | string | Blob;

export interface PutToSignedUrlResult {
  ok: boolean;
  status: number;
}

export type PutToSignedUrlFn = (
  url: string,
  headers: Readonly<Record<string, string>>,
  body: UploadBody,
) => Promise<PutToSignedUrlResult>;

export interface UploadOrchestratorDeps {
  requestUploadGrant: (
    input: RequestCustomerDocumentUploadGrantDto,
  ) => Promise<RequestUploadGrantResponseDto | MeFetchError>;
  finalizeUpload: (
    documentId: string,
    input: FinalizeCustomerDocumentUploadDto,
  ) => Promise<CustomerDocumentMeDto | MeFetchError>;
  putToSignedUrl: PutToSignedUrlFn;
}

export interface UploadFlowInput {
  metadata: RequestCustomerDocumentUploadGrantDto;
  body: UploadBody;
  uploadedByteSize: number;
}

export function isMeFetchErrorResult(
  v: unknown,
): v is MeFetchError {
  if (!v || typeof v !== 'object') return false;
  const obj = v as { kind?: unknown };
  return (
    typeof obj.kind === 'string' &&
    ['session_expired', 'forbidden', 'not_found', 'conflict', 'network', 'unknown'].includes(
      obj.kind,
    )
  );
}

export async function executeUploadFlow(
  deps: UploadOrchestratorDeps,
  input: UploadFlowInput,
): Promise<CustomerDocumentMeDto | MeFetchError> {
  if (input.uploadedByteSize <= 0) {
    return meError('unknown', '文件大小必须大于 0 字节');
  }
  const grantResp = await deps.requestUploadGrant(input.metadata);
  if (isMeFetchErrorResult(grantResp)) return grantResp;

  let putRes: PutToSignedUrlResult;
  try {
    putRes = await deps.putToSignedUrl(
      grantResp.grant.uploadUrl,
      grantResp.grant.requiredHeaders,
      input.body,
    );
  } catch {
    return meError('network', '上传到对象存储失败，请稍后重试');
  }

  if (!putRes.ok) {
    return meError('unknown', `对象存储拒绝了上传请求（HTTP ${putRes.status}）`);
  }

  return deps.finalizeUpload(grantResp.document.id, {
    expectedVersion: grantResp.document.version,
    uploadedByteSize: input.uploadedByteSize,
  });
}

export async function defaultPutToSignedUrl(
  url: string,
  headers: Readonly<Record<string, string>>,
  body: UploadBody,
): Promise<PutToSignedUrlResult> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { ...headers },
    body,
  });
  return { ok: res.ok, status: res.status };
}
